
import { useState } from 'react';
import { scuba3sParameters } from '../../mocks/poolData';
import { scuba3sHistoricalData } from '../../mocks/scuba3sData';

export default function GraficosScuba3s() {
  const [selectedParams, setSelectedParams] = useState<string[]>(['scuba_ph', 'scuba_chlorine', 'scuba_alkalinity']);
  const [timeRange, setTimeRange] = useState('7dias');
  const [chartType, setChartType] = useState<'linea' | 'barras' | 'area'>('linea');

  const toggleParam = (paramId: string) => {
    setSelectedParams(prev =>
      prev.includes(paramId)
        ? prev.filter(id => id !== paramId)
        : [...prev, paramId]
    );
  };

  const getParamColor = (index: number) => {
    const colors = [
      { line: '#14b8a6', bg: 'rgba(20, 184, 166, 0.1)' },
      { line: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
      { line: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)' },
      { line: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },
      { line: '#06b6d4', bg: 'rgba(6, 182, 212, 0.1)' },
      { line: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
      { line: '#f97316', bg: 'rgba(249, 115, 22, 0.1)' },
      { line: '#ec4899', bg: 'rgba(236, 72, 153, 0.1)' },
      { line: '#6366f1', bg: 'rgba(99, 102, 241, 0.1)' }
    ];
    return colors[index % colors.length];
  };

  const generateChartPoints = (paramId: string, index: number) => {
    const data = scuba3sHistoricalData.map(d => d[paramId as keyof typeof d] as number);
    const maxVal = Math.max(...data);
    const minVal = Math.min(...data);
    const range = maxVal - minVal || 1;
    
    const points = data.map((val, i) => {
      const x = 40 + (i * (520 / (data.length - 1)));
      const y = 180 - ((val - minVal) / range) * 140;
      return `${x},${y}`;
    }).join(' ');

    return { points, data, maxVal, minVal };
  };

  const getStatus = (paramId: string, value: number) => {
    const param = scuba3sParameters.find(p => p.id === paramId);
    if (!param) return 'optimal';
    if (value < param.warningRange.min || value > param.warningRange.max) return 'critical';
    if (value < param.optimalRange.min || value > param.optimalRange.max) return 'warning';
    return 'optimal';
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-xl flex items-center justify-center">
            <i className="ri-line-chart-line text-white text-2xl"></i>
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-800">Gráficos Scuba3s</h3>
            <p className="text-sm text-slate-500">Visualización de mediciones del dispositivo</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 cursor-pointer"
          >
            <option value="24h">Últimas 24h</option>
            <option value="7dias">7 días</option>
            <option value="30dias">30 días</option>
            <option value="3meses">3 meses</option>
          </select>
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setChartType('linea')}
              className={`px-3 py-1 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                chartType === 'linea' ? 'bg-white shadow text-teal-600' : 'text-gray-600'
              }`}
            >
              <i className="ri-line-chart-line"></i>
            </button>
            <button
              onClick={() => setChartType('barras')}
              className={`px-3 py-1 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                chartType === 'barras' ? 'bg-white shadow text-teal-600' : 'text-gray-600'
              }`}
            >
              <i className="ri-bar-chart-line"></i>
            </button>
            <button
              onClick={() => setChartType('area')}
              className={`px-3 py-1 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                chartType === 'area' ? 'bg-white shadow text-teal-600' : 'text-gray-600'
              }`}
            >
              <i className="ri-stack-line"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Selector de Parámetros */}
      <div className="mb-6">
        <p className="text-sm font-medium text-slate-700 mb-3">Seleccionar parámetros:</p>
        <div className="flex flex-wrap gap-2">
          {scuba3sParameters.map((param, index) => (
            <button
              key={param.id}
              onClick={() => toggleParam(param.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap flex items-center space-x-1 ${
                selectedParams.includes(param.id)
                  ? 'text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              style={selectedParams.includes(param.id) ? { backgroundColor: getParamColor(index).line } : {}}
            >
              <i className={param.icon}></i>
              <span>{param.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Gráfico Principal */}
      <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-4 mb-6">
        <svg viewBox="0 0 600 220" className="w-full h-64">
          {/* Grid */}
          <defs>
            <pattern id="grid" width="60" height="40" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 40" fill="none" stroke="#e5e7eb" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect x="40" y="20" width="520" height="160" fill="url(#grid)" />

          {/* Ejes */}
          <line x1="40" y1="180" x2="560" y2="180" stroke="#94a3b8" strokeWidth="1" />
          <line x1="40" y1="20" x2="40" y2="180" stroke="#94a3b8" strokeWidth="1" />

          {/* Etiquetas eje X */}
          {scuba3sHistoricalData.slice(0, 7).map((data, i) => (
            <text
              key={i}
              x={40 + (i * (520 / 6))}
              y="198"
              textAnchor="middle"
              className="text-xs fill-gray-500"
              fontSize="10"
            >
              {data.date.split('-').slice(1).join('/')}
            </text>
          ))}

          {/* Líneas de datos */}
          {selectedParams.map((paramId, index) => {
            const { points, data } = generateChartPoints(paramId, index);
            const color = getParamColor(index);

            if (chartType === 'area') {
              const areaPoints = `40,180 ${points} 560,180`;
              return (
                <g key={paramId}>
                  <polygon points={areaPoints} fill={color.bg} />
                  <polyline points={points} fill="none" stroke={color.line} strokeWidth="2" />
                  {data.map((val, i) => (
                    <circle
                      key={i}
                      cx={40 + (i * (520 / (data.length - 1)))}
                      cy={180 - ((val - Math.min(...data)) / (Math.max(...data) - Math.min(...data) || 1)) * 140}
                      r="4"
                      fill={color.line}
                      className="hover:r-6 transition-all"
                    />
                  ))}
                </g>
              );
            }

            if (chartType === 'barras') {
              const barWidth = 50 / selectedParams.length;
              return (
                <g key={paramId}>
                  {data.map((val, i) => {
                    const height = ((val - Math.min(...data)) / (Math.max(...data) - Math.min(...data) || 1)) * 140;
                    return (
                      <rect
                        key={i}
                        x={40 + (i * (520 / (data.length - 1))) - barWidth / 2 + (index * barWidth / selectedParams.length)}
                        y={180 - height}
                        width={barWidth - 2}
                        height={height}
                        fill={color.line}
                        rx="2"
                        className="hover:opacity-80 transition-opacity"
                      />
                    );
                  })}
                </g>
              );
            }

            return (
              <g key={paramId}>
                <polyline points={points} fill="none" stroke={color.line} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                {data.map((val, i) => (
                  <circle
                    key={i}
                    cx={40 + (i * (520 / (data.length - 1)))}
                    cy={180 - ((val - Math.min(...data)) / (Math.max(...data) - Math.min(...data) || 1)) * 140}
                    r="5"
                    fill="white"
                    stroke={color.line}
                    strokeWidth="2"
                    className="hover:r-7 transition-all cursor-pointer"
                  />
                ))}
              </g>
            );
          })}
        </svg>

        {/* Leyenda */}
        <div className="flex flex-wrap justify-center gap-4 mt-4">
          {selectedParams.map((paramId, index) => {
            const param = scuba3sParameters.find(p => p.id === paramId);
            if (!param) return null;
            return (
              <div key={paramId} className="flex items-center space-x-2">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: getParamColor(index).line }}
                ></span>
                <span className="text-xs text-gray-600">{param.name}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tarjetas de Valores Actuales */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {scuba3sParameters.map((param) => {
          const status = getStatus(param.id, param.currentValue);
          return (
            <div
              key={param.id}
              className={`p-3 rounded-xl border-2 transition-all ${
                status === 'critical' ? 'border-red-300 bg-red-50' :
                status === 'warning' ? 'border-amber-300 bg-amber-50' :
                'border-teal-200 bg-teal-50/50'
              }`}
            >
              <div className="flex items-center space-x-2 mb-2">
                <i className={`${param.icon} ${
                  status === 'critical' ? 'text-red-500' :
                  status === 'warning' ? 'text-amber-500' :
                  'text-teal-500'
                }`}></i>
                <span className="text-xs font-medium text-slate-600 truncate">{param.name}</span>
              </div>
              <p className={`text-lg font-bold ${
                status === 'critical' ? 'text-red-700' :
                status === 'warning' ? 'text-amber-700' :
                'text-teal-700'
              }`}>
                {param.currentValue}
                <span className="text-xs font-normal ml-1">{param.unit}</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Rango: {param.optimalRange.min}-{param.optimalRange.max}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
